import React from 'react';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as WorksheetIcon } from 'assets/icons/worksheet.svg';
import { ReactComponent as CollegeIcon } from 'assets/icons/college.svg';
import { Badge } from '@material-ui/core';

const RADIAN = Math.PI / 180;

const useStyles = makeStyles((theme) => ({
  statTitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 2,
    lineHeight: 2,
  },
  statNumber: {
    fontFamily: 'Arimo',
    paddingRight: theme.spacing(1),
  },

}));

const StatusIcon = withStyles((theme) => ({
  root: {
    borderRadius: '50%',
    marginRight: theme.spacing(3),
    width: 70,
    height: 70,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
}))(Card);

const StatCard = ({ value, unit, label, icon }) => {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center">
      <StatusIcon>{icon}</StatusIcon>
      <Box>
        <Typography className={classes.statTitle}>{label}</Typography>
        <Box display="flex" alignItems="baseline">
          <Typography
            className={classes.statNumber}
            color="primary"
            variant="h2"
          >
            {value}
          </Typography>
          {unit && (
            <Typography color="primary" variant="h5">
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Stats = ({ data }) => {
  const {
    documents,
    appliedCount,
    nextUniDue,
    exercises_done: { aggregate },
    exercises_inprogress,
    exercises_notstarted,
    exercises_count,
  } = data;
  const grouped = groupBy(documents, 'status');
  const worksheetCount = aggregate.count;
  const dueDate = moment(nextUniDue[0]?.due_date);

  const totalExercisesCount = exercises_count.aggregate.count;
  const exerciseGraphData = [
    {
      label: 'WRITTEN',
      value: aggregate.count,
      color: '#25C186',
      total: { value: totalExercisesCount, type: 'EXERCISES' },
    },
    {
      label: 'IN PROGRESS',
      value: exercises_inprogress.aggregate.count,
      color: '#F79900',
    },
    {
      label: 'NOT STARTED',
      value: exercises_notstarted.aggregate.count,
      color: '#DEDEDE',
      labelColor: '#000000',
    },
  ];

  const essayGraphData = [
    {
      label: 'COMPLETED',
      value: grouped['done']?.length || 0,
      color: '#25C186',
      total: { value: documents.length, type: 'ESSAYS' },
    },
    {
      label: 'IN REVIEW',
      value:
        (grouped['cc_review']?.length || 0) +
        (grouped['hc_review']?.length || 0),
      color: '#EB5E28',
    },
    {
      label: 'IN DRAFT',
      value: grouped['draft']?.length || 0,
      color: '#F79900',
    },
    {
      label: 'NOT STARTED',
      value: grouped['new']?.length || 0,
      color: '#DEDEDE',
      labelColor: '#000000',
    },
  ];

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    value,
    payload,
    index,
    x,
    y,
  }) => {
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1);
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';
    return (
      <g>
        {index === 0 && (
          <>
            <text
              x={cx}
              y={cy}
              dy={8}
              textAnchor="middle"
              fill="#000"
              fontSize={48}
              fontWeight={700}
            >
              {payload.total.value}
            </text>
            <text
              x={cx}
              y={cy + 20}
              dy={8}
              textAnchor="middle"
              fill="#000"
              fontSize={12}
              fontWeight={700}
            >
              {payload.total.type}
            </text>
          </>
        )}
        {value && (
          <>
            <text
              x={ex + (cos >= 0 ? 1 : -1) * 0}
              y={ey -9}
              textAnchor={textAnchor}
              fill={payload.labelColor || payload.color}
              fontSize={12}
              fontWeight={700}
              letterSpacing={2}
            >
              {payload.label}
            </text>
            <circle
              fontSize={13}
              cx={ex + (cos >= 0 ? 1 : -1) * 12}
              cy={ey + 14}
              fill={payload.color}
              class="SVGBadge-svgBackground"
              r="13"
            />
            <text
              fontSize={13}
              x={ex + (cos >= 0 ? 1 : -1) * 12}
              y={ey + 0.5}
              dy={18}
              textAnchor={'middle'}
              fill={payload.color === '#DEDEDE' ? '#696D75' : '#fff'}
            >
              {value}
            </text>
          </>
        )}
      </g>
    );
  };

  return (
    <>
      <Box marginBottom={{ xs: 4, md: 10 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Next App due in"
              value={moment.utc(dueDate, 'YYYY-MM-DD').diff(moment.utc(), 'd')}
              unit="Days"
              icon={<CalendarIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Exercises written"
              value={worksheetCount}
              icon={<WorksheetIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Colleges applied to"
              value={appliedCount.aggregate.count}
              icon={<CollegeIcon />}
            />
          </Grid>
        </Grid>
      </Box>
      <Box marginTop={-6}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardHeader
                title="Exercises"
                titleTypographyProps={{ variant: 'h5' }}
              />
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      key={Math.random()}
                      label={renderCustomizedLabel}
                      labelLine={false}
                      data={exerciseGraphData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={94}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {exerciseGraphData.map((entry, index) => (
                        <Cell strokeWidth={0} key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardHeader
                title="Essays"
                titleTypographyProps={{ variant: 'h5' }}
              />
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      key={Math.random()}
                      label={renderCustomizedLabel}
                      labelLine={false}
                      data={essayGraphData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={94}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {essayGraphData.map((entry, index) => (
                        <Cell strokeWidth={0} key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
      </>
      );
};

      export default Stats;
